import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import { Box, Chip, Divider, Drawer, Stack, Typography } from "@mui/material";
import "./AccountProfileSidebarComponent.css";
import { useLayout } from "components/layout/LayoutComponentContext";
import { OverflowText } from "components";
import {
  allowViewUserManagement,
  SHOWN_USER_ROLE,
} from "consts/UserManagement";
import { capitalize } from "lodash";

const AccountProfileSidebarComponent = ({
  isProfileOpen,
  onChangePassword,
  onCloseProfile,
  onEditProfile,
  userProfile,
  onChangeLogo,
  onOpenActivityLog,
  onOpenUserManagement,
}) => {
  const { companyProfile, admin } = useLayout();
  const usersToDisplay =
    userProfile?.role === "COMPANY ADMIN"
      ? companyProfile?.company_users
      : admin;

  return (
    <Drawer
      className='account-profile-drawer'
      anchor='right'
      open={isProfileOpen}
    >
      <Stack
        className='header'
        direction='row'
        justifyContent='space-between'
        alignItems='center'
      >
        <Typography fontWeight={700}>Profile</Typography>
        <img
          alt='close-profile-icon'
          className='cursor-pointer'
          src='/images/close-profile-icon.svg'
          onClick={onCloseProfile}
        />
      </Stack>
      <Stack className='container' spacing={3}>
        <Stack spacing={3} alignItems='center'>
          {userProfile?.avatar_path ? (
            <img
              alt='account-profile-icon'
              className='h-40 w-40 rounded-full shrink-0 object-cover'
              src={userProfile.avatar_path}
            />
          ) : (
            <AccountCircleRoundedIcon
              viewBox='2 2 20 20'
              aria-label='account-profile-image-default'
              className='p-0'
              sx={{ height: "160px", width: "160px", color: "#ccc" }}
            />
          )}
          <Stack
            direction='row'
            justifyContent='center'
            alignItems='center'
            spacing={2}
          >
            <OverflowText
              text={userProfile?.full_name}
              textClassName='font-bold text-2xl'
              maxChar={20}
            />
            {companyProfile?.subscription_type === "PRO" && (
              <Box bgcolor='#556FB5' className='px-2 py-0.5 rounded-[4px]'>
                <Typography
                  fontWeight={700}
                  color='white'
                  className='!leading-[22px]'
                >
                  PRO
                </Typography>
              </Box>
            )}
          </Stack>
        </Stack>
        {/* Account Information */}
        <Stack spacing={3}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography fontWeight={700}>Account Information</Typography>
            <Stack direction='row' spacing={2}>
              <Typography
                className='hover:cursor-pointer text-[#556FB5]'
                fontSize={12}
                fontWeight={800}
                onClick={onEditProfile}
              >
                Edit
              </Typography>
              <Typography
                className='hover:cursor-pointer text-[#556FB5]'
                fontSize={12}
                fontWeight={800}
                onClick={onChangePassword}
              >
                Change Password
              </Typography>
            </Stack>
          </Stack>
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography fontSize={12}>E-mail address</Typography>
              <Typography fontWeight={600} fontSize={14}>
                {userProfile?.email}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography fontSize={12}>Phone Number</Typography>
              <Typography fontWeight={600} fontSize={14}>
                {userProfile?.phone_number}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography fontSize={12}>Role</Typography>
              <Typography fontWeight={600} fontSize={14}>
                {SHOWN_USER_ROLE?.[userProfile?.role] ||
                  capitalize(userProfile?.role)}
              </Typography>
            </Stack>
            <Typography
              className='hover:cursor-pointer text-[#556FB5]'
              fontSize={12}
              fontWeight={800}
              onClick={onOpenActivityLog}
            >
              View Account Activity Log
            </Typography>
          </Stack>
        </Stack>
        {/* User Management */}
        {allowViewUserManagement.includes(userProfile?.role) && (
          <Stack spacing={3}>
            <Divider />
            <Stack spacing={2}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography fontWeight={700}>User Management</Typography>
                <Typography
                  className='hover:cursor-pointer text-[#556FB5]'
                  fontSize={12}
                  fontWeight={800}
                  onClick={onOpenUserManagement}
                >
                  View All
                </Typography>
              </Stack>
              <Box>
                {usersToDisplay ? (
                  <Stack divider={<Divider />}>
                    {usersToDisplay.slice(0, 5).map((user) => {
                      return (
                        <Stack
                          direction='row'
                          spacing={2}
                          className='py-4'
                          alignItems='center'
                        >
                          {user?.avatar_path ? (
                            <img
                              alt='account-profile-image'
                              src={user.avatar_path}
                              className='w-10 h-10 rounded-full shrink-0 object-cover'
                            />
                          ) : (
                            <AccountCircleRoundedIcon
                              viewBox='2 2 20 20'
                              aria-label='account-profile-image-default'
                              className='p-0'
                              sx={{
                                height: "40px",
                                width: "40px",
                                color: "#ccc",
                              }}
                            />
                          )}
                          <Stack spacing={1} justifyContent='center'>
                            <Typography fontWeight={600} fontSize={14}>
                              {user?.name}
                            </Typography>
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1}
                            >
                              <Typography
                                fontSize={12}
                                color='#5F737F'
                                className='capitalize'
                              >
                                {SHOWN_USER_ROLE?.[user?.role] ||
                                  capitalize(user?.role)}
                              </Typography>
                              <Box className='rounded-full h-1 w-1 bg-[#5F737F]'></Box>
                              {user?.status === "ACTIVE" ? (
                                <Chip
                                  className='capitalize !bg-[#CAFAD2] !text-[#039E5A] !font-medium'
                                  size='small'
                                  label={user?.status.toLowerCase()}
                                ></Chip>
                              ) : (
                                <Chip
                                  className='capitalize !bg-[#FDDCD3] !text-[#E52A49] !font-medium'
                                  size='small'
                                  label={user?.status.toLowerCase()}
                                ></Chip>
                              )}
                            </Stack>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                ) : (
                  <Stack
                    justifyContent='center'
                    alignItems='center'
                    spacing={2}
                  >
                    <img
                      alt='empty-admin-icon'
                      src='/images/admin-empty-state.svg'
                    />
                    <Typography
                      className='cursor-pointer'
                      color='#556FB5'
                      fontWeight={800}
                      fontSize={12}
                    >
                      Create New Account
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Stack>
        )}
        {/* Client Information */}
        {companyProfile && (
          <Stack spacing={3}>
            <Divider />
            <Stack spacing={3}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography fontWeight={700}>Client Information</Typography>
                {userProfile?.role === "COMPANY ADMIN" && (
                  <Typography
                    className='hover:cursor-pointer text-[#556FB5]'
                    fontSize={12}
                    fontWeight={800}
                    onClick={onChangeLogo}
                  >
                    Change Logo
                  </Typography>
                )}
              </Stack>
              <Stack justifyContent='center' direction='row'>
                {companyProfile?.logo_url ? (
                  <img
                    className='w-16 h-16 rounded-full shrink-0 object-cover'
                    alt='logo.png'
                    src={companyProfile.logo_url}
                  ></img>
                ) : (
                  <AccountCircleRoundedIcon
                    viewBox='2 2 20 20'
                    aria-label='account-profile-image-default'
                    sx={{ height: "64px", width: "64px", color: "#ccc" }}
                  />
                )}
              </Stack>
              <Stack spacing={2}>
                <Stack spacing={1}>
                  <Typography fontSize={12}>Client Name</Typography>
                  <Typography fontWeight={600} fontSize={14}>
                    {companyProfile?.name}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography fontSize={12}>Listing Year</Typography>
                  <Typography fontWeight={600} fontSize={14}>
                    {companyProfile?.year_established}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography fontSize={12}>Industry</Typography>
                  <Typography fontWeight={600} fontSize={14}>
                    {companyProfile?.industry_name}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
};

export default AccountProfileSidebarComponent;
